import React, { useEffect, useState } from "react";

import Axios from "axios";
import moment from "moment-timezone";

import { dirname } from "../../util/resourcePath";
import { useUser } from "../../util/user";
import GYAxios from "../../util/GYAxios";
import siteDataFormatter from '../../util/siteDataFormatter';

import LoaderGraph from "../general/LoaderGraph";
import OverviewMap from "./OverviewMap";
import OverviewMonthlyProdtable from "./OverviewMonthlyProdtable";
import { Dashboard, } from "./Dashboard";
import OverviewSiteTables from "./OverviewSiteTables";
import { useKeycloak } from "@react-keycloak/web";
import { getGYUserCountry } from "../../util/roles";


function OverviewContent() {
	const { keycloak } = useKeycloak();
	const KCuser = useUser();
	const [logo, setLogo] = useState("");
	const [tempKey, setTempKey] = useState("");
	const [user, setUser] = useState(null);
	const [siteData, setSiteData] = useState([]);
	const [formattedSites, setFormattedSites] = useState([]);
	const directory = dirname();

	// add references for easy access to siteData;
	const [ siteDataLive, siteDataMonthEnergy, siteDataYtdEnergy, siteDataTodayEnergy, siteDataLocation, siteDataPrice ] = siteData;

	const options = {
		center: {
			lat: 13.7469,
			lng: 100.535,
		},
		zoom: 6,
	};

	function fetchSiteData(user) {
		const dateObj = moment();
		const today = dateObj.format("YYYY-MM-DD");
		const yesterday = dateObj.subtract(1, "day").format("YYYY-MM-DD");
		const currentMonth = moment().format("YYYYMM");
		if (user != null) {
			// P10 request URI
			let URI = "";

			// Filter column for request
			let column = "";

			// Filter value for request
			let value = "";

			// Solar Admin
			if (user.type === "admin" && !user.roles.includes("_GY:External")) {
				column = "admin";
				value = "all";
				URI = `/p10/live/multi_site/admin/all`;
			}

			// External Admin
			else if (user.type === "admin" && user.roles.includes("_GY:External")) {
				const userCountry = getGYUserCountry(user.roles);
				column = "country";
				value = userCountry;
				URI = `/p10/live/multi_site/country/${userCountry}`;
			}
			// Client access
			else if (user.type === "client") {
				const clientID = user.id;

				column = "client_id";
				value = clientID;
				URI = `/p10/live/multi_site/client_id/${clientID}`;
			}

			// Multiple sites
			else {
				const siteID = user.id;

				column = "site_id";
				value = siteID;
				URI = `/p10/live/multi_site/site_id/${siteID}`;
			}

			// Multi-requests
			const reqLive = GYAxios.get(URI);

			// Monthly Data Request
			const dateFormat = "YYYYMMDD";
			const monthlyFromDate = moment().startOf("month").format(dateFormat);
			const monthlyToDate = moment().endOf("month").format(dateFormat);

			const reqMonthlyURI = `/monthly/filter/${column}/${value}/period/${monthlyFromDate}/${monthlyToDate}/order/dateData/DESC`;
			const reqMonthEnergy = GYAxios.get(reqMonthlyURI);

			// Daily Data Request - Yesterday and Today
			const reqYtdURI = `/daily/filter/${column}/${value}/period/${yesterday}/${yesterday}/order/dateData/DESC`;
			const reqYtdEnergy = GYAxios.get(reqYtdURI);

			const reqTodayURI = `/daily/filter/${column}/${value}/period/${today}/${today}/order/dateData/DESC`;
			const reqTodayEnergy = GYAxios.get(reqTodayURI);

			// Location Data Request
			const reqLocationURI = `/general_info/location/filter/${column}/${value}`;
			const reqLocation = GYAxios.get(reqLocationURI);

			// we no longer require price data to know that site is CO-ed or not
			// Price Data Request
			const reqPriceURI = `/invoice/price/filter/${column}/${value}/month/${currentMonth}`;
			const reqPrice = GYAxios.get(reqPriceURI);

			const site_alarms = `/alarms/site_alarms`;
			const reqSiteAlarms = GYAxios.get(site_alarms);

			const sitesAlarmsURI = `/alarms/sites_alarms/${column}/${value}`;
			const reqSitesAlarms = GYAxios.get(sitesAlarmsURI);
			

			const reqArray = [
				reqLive,
				reqMonthEnergy,
				reqYtdEnergy,
				reqTodayEnergy,
				reqLocation,
				reqPrice,
				reqSiteAlarms,
				reqSitesAlarms,
			];

			Axios.all(reqArray).then(
				Axios.spread((...responses) => {
					setSiteData(responses); // update siteData
				})
			);
		}
	}

	useEffect(() => {
		if(keycloak.token && keycloak.token !== tempKey)
			setTempKey(keycloak.token);
	}, [keycloak.token, tempKey]);

	useEffect(() => {
		// set userType and logo
		const user = KCuser;

		// GY users
		if (user.roles.includes("_GY:Base")) {
			setUser({ type: "admin", roles: user.roles });
			setLogo(`${directory}/images/gy_g.png`);
		}
		// Client users}
		else {
			const keycloakID = user.sub;
			const usersURI = `/users/filter?column=keycloak_id&value=${keycloakID}`;

			if (tempKey != "") {
				GYAxios.get(usersURI).then((res) => {
					const userInfo =
						res.data.error == null && res.data.length > 0 ? res.data[0] : false;

					// Client access
					if (user.roles.includes("_CLIENT:Multi")) {
						if (userInfo.site_id != null && userInfo.site_id !== "all")
							setUser({ type: "client_multi", id: userInfo.site_id, roles: user.roles });
						else setUser({ type: "client", id: userInfo.client_id, roles: user.roles });

						const slug = userInfo ? userInfo.slug : "";
						setLogo(`${directory}/images/${slug}_logo.png`);
					}
				});
			}
		}
	}, [KCuser, tempKey]);

	useEffect(() => {
		// fetch site data once userType is updated
		if (user != null) fetchSiteData(user);
	}, [user]);

	useEffect(() => {
		// formatted site data
		siteDataFormatter(siteData, user, formattedSites => {
			setFormattedSites(formattedSites);
		});
	}, [siteData, user]);

	return (
		<>
			{
				<div className="container90" id="site-active">
					<div className="row map_info">
						{siteData.length !== 0 && logo !== "" ? (
							<Dashboard user={user} siteData={siteData} clientLogo={logo} />
						) : (
							<LoaderGraph style={{ height: "100vh" }} />
						)}

						{/* Table showing sites data*/}
						{siteData.length !== 0 ? (
							<>
								<OverviewSiteTables user={user} siteData={siteData} />
							</>
						) : (
							<LoaderGraph style={{ height: "100vh" }} />
						)}

						<>
							{/* Monthly Production Table - Only for multi sites */}
							{KCuser.roles.includes(
								"overview:OverviewMonthlyProdtable:_view"
							) &&
								user != null && <OverviewMonthlyProdtable client={user} />}

							{/* Google Map having animation so put at the first*/}
							{siteDataLocation?.data?.length ? (
								<OverviewMap
									formattedSites={formattedSites}
									locations={siteDataLocation.data}
									options={options}
								/>
							) : (
								<LoaderGraph style={{ height: "100vh" }} />
							)}
						</>
					</div>
				</div>
			}
		</>
	);
}

export default OverviewContent;
